import { Box, Checkbox, Code, Divider, Group, NumberInput, Select, Stack, TextInput, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { closeModal, openModal } from '@mantine/modals';
import { Editor } from '@tiptap/react';
import P3Regular from 'components/typography/P3Regular';
import P4Regular from 'components/typography/P4Regular';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useFormSections } from 'pages/revisions-module/template-editor/editors/form/section/FormSectionsDataProvider';
import { IFormSection } from 'pages/revisions-module/template-editor/editors/form/types';
import {
  DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT,
  DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY,
  DeviceMeasurementsPlaceholderFormat,
  DeviceMeasurementsPlaceholderWidth,
} from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/device-measurements/DeviceMeasurementsPlaceholder';
import { getDeviceMeasurementsPlaceholderColumns } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/device-measurements/device-measurements-placeholder-columns';

import Button from 'components/Button';
const MODAL_ID = 'device-measurements-placeholder-configurator';
const EVENT_NAME = 'open-device-measurements-configurator';

interface DeviceMeasurementsConfiguratorValues {
  sectionId: string;
  format: DeviceMeasurementsPlaceholderFormat;
}

/**
 * Creates a default auto-width configuration for a column.
 */
function createDefaultWidth(key: string): DeviceMeasurementsPlaceholderWidth {
  return { key, unit: 'auto' };
}

/**
 * Normalizes a column width entry while keeping the selected unit in form state.
 */
function normalizeWidthForForm(width: DeviceMeasurementsPlaceholderWidth): DeviceMeasurementsPlaceholderWidth {
  if (width.unit === 'auto') {
    return { key: width.key, unit: 'auto' };
  }

  return {
    key: width.key,
    unit: width.unit,
    value: Number.isInteger(width.value) && (width.value ?? 0) > 0 ? width.value : undefined,
  };
}

/**
 * Normalizes a column width entry before it is persisted.
 */
function normalizeWidthForSave(width: DeviceMeasurementsPlaceholderWidth): DeviceMeasurementsPlaceholderWidth {
  if (width.unit === 'auto') {
    return { key: width.key, unit: 'auto' };
  }

  if (Number.isInteger(width.value) && (width.value ?? 0) > 0) {
    return {
      key: width.key,
      unit: width.unit,
      value: width.value,
    };
  }

  return { key: width.key, unit: 'auto' };
}

/**
 * Checks whether two width lists are identical.
 */
function areWidthsEqual(a: DeviceMeasurementsPlaceholderWidth[], b: DeviceMeasurementsPlaceholderWidth[]) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Checks whether two column lists are identical.
 */
function areColumnsEqual(
  a: DeviceMeasurementsPlaceholderFormat['columns'],
  b: DeviceMeasurementsPlaceholderFormat['columns']
) {
  return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
}

/**
 * Preserves an explicitly saved heading, including an empty string override.
 */
function resolveHeading(savedHeading: string | undefined, fallbackHeading: string) {
  return savedHeading === undefined ? fallbackHeading : savedHeading;
}

/**
 * The configurator for the device measurements placeholder.
 */
function DeviceMeasurementsConfigurator({ editor, sections }: { editor: Editor; sections: IFormSection[] }) {
  const initialValues = useMemo(() => {
    const attributes = editor.getAttributes('deviceMeasurementsPlaceholder');

    return {
      sectionId: String(attributes.sectionId || ''),
      format: {
        ...DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT,
        ...(attributes.format as DeviceMeasurementsPlaceholderFormat | undefined),
      },
    } satisfies DeviceMeasurementsConfiguratorValues;
  }, []);

  const form = useForm<DeviceMeasurementsConfiguratorValues>({ initialValues });

  const options = useMemo(
    () =>
      sections
        .filter(({ tab }) => tab === 'measurements')
        .map((section) => ({ value: section.id, label: section.name || '(Bez názvu)' })),
    [sections]
  );

  const formatOptions = useMemo(
    () => [
      { value: 'table', label: 'Tabuľka' },
      { value: 'custom', label: 'Vlastný formát (HTML)' },
    ],
    []
  );

  const widthUnitOptions = useMemo(
    () => [
      { value: 'auto', label: 'auto' },
      { value: 'mm', label: 'mm' },
      { value: '%', label: '%' },
    ],
    []
  );

  const selectedSection = useMemo(
    () => sections.find(({ id }) => id === form.values.sectionId),
    [sections, form.values.sectionId]
  );

  const measurementColumns = useMemo(
    () =>
      getDeviceMeasurementsPlaceholderColumns(selectedSection).map((column) => ({
        ...column,
        placeholder: column.key,
      })),
    [selectedSection]
  );

  const tableColumns = useMemo(
    () => [
      {
        key: DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY,
        label: 'Číslo',
        placeholder: 'index',
        type: 'number',
      },
      ...measurementColumns,
    ],
    [measurementColumns]
  );

  const currentColumnKeys = useMemo(() => tableColumns.map(({ key }) => key), [tableColumns]);

  const visibleCustomPlaceholders = useMemo(
    () => tableColumns.map(({ placeholder }) => `{${placeholder}}`),
    [tableColumns]
  );

  useEffect(() => {
    const widthsByKey = new Map(form.values.format.widths.map((width) => [width.key, width]));
    const currentKeySet = new Set(currentColumnKeys);

    const alignedWidths = currentColumnKeys.map((key) => widthsByKey.get(key) ?? createDefaultWidth(key));
    const staleWidths = form.values.format.widths.filter((width) => !currentKeySet.has(width.key));
    const nextWidths = [...alignedWidths, ...staleWidths].map(normalizeWidthForForm);

    if (!areWidthsEqual(nextWidths, form.values.format.widths)) {
      form.setFieldValue('format.widths', nextWidths);
    }
  }, [currentColumnKeys, form, form.values.format.widths]);

  useEffect(() => {
    const columnsByKey = new Map((form.values.format.columns ?? []).map((column) => [column.key, column]));
    const nextColumns = measurementColumns.map((column) => ({
      key: column.key,
      label: resolveHeading(columnsByKey.get(column.key)?.label, column.label),
      type: column.type,
      show: columnsByKey.get(column.key)?.show ?? true,
      suffix: column.suffix,
      precision: column.precision,
    }));

    if (!areColumnsEqual(nextColumns, form.values.format.columns)) {
      form.setFieldValue('format.columns', nextColumns);
    }
  }, [form, form.values.format.columns, measurementColumns]);

  useEffect(() => {
    const nextIndexColumn = {
      key: DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY,
      label: resolveHeading(form.values.format.indexColumn?.label, 'Číslo'),
      show: form.values.format.indexColumn?.show ?? true,
    };

    if (JSON.stringify(nextIndexColumn) !== JSON.stringify(form.values.format.indexColumn ?? {})) {
      form.setFieldValue('format.indexColumn', nextIndexColumn);
    }
  }, [form, form.values.format.indexColumn]);

  const close = useCallback(() => closeModal(MODAL_ID), []);

  const submit = useCallback(
    ({ sectionId, format }: DeviceMeasurementsConfiguratorValues) => {
      const currentKeySet = new Set(currentColumnKeys);
      const widths = format.widths
        .filter((width) => currentKeySet.has(width.key) || width.key === DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY)
        .map(normalizeWidthForSave);
      const columns = measurementColumns.map((column) => {
        const savedColumn = format.columns?.find(({ key }) => key === column.key);

        return {
          key: column.key,
          label: resolveHeading(savedColumn?.label, column.label),
          type: column.type,
          show: savedColumn?.show ?? true,
          suffix: column.suffix,
          precision: column.precision,
        };
      });
      const indexColumn = {
        key: DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY,
        label: resolveHeading(format.indexColumn?.label, 'Číslo'),
        show: format.indexColumn?.show ?? true,
      };

      editor
        .chain()
        .focus()
        .setDeviceMeasurementsPlaceholderConfig({
          sectionId,
          format: {
            ...DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT,
            ...format,
            widths,
            columns,
            indexColumn,
          },
        })
        .run();
      close();
    },
    [editor, close, currentColumnKeys, measurementColumns]
  );

  return (
    <form onSubmit={form.onSubmit(submit)}>
      <Stack spacing={24}>
        <Select size="sm" required label="Tabuľka meraní" data={options} {...form.getInputProps('sectionId')} />
        <Select size="sm" required label="Formát výstupu" data={formatOptions} {...form.getInputProps('format.type')} />
        {form.values.format.type === 'custom' && (
          <Fragment>
            <Divider mt={4} color="gray.0" />
            <Stack spacing={4}>
              <Textarea
                label="Šablóna riadku"
                size="sm"
                required
                minRows={1}
                autosize
                {...form.getInputProps('format.lineTemplate')}
              />
              <P4Regular c="gray.6">
                Použite zástupné znaky:{' '}
                {visibleCustomPlaceholders.map((placeholder) => (
                  <Fragment key={placeholder}>
                    <Code c="gray.6">{placeholder}</Code>{' '}
                  </Fragment>
                ))}
              </P4Regular>
            </Stack>
            <Textarea minRows={1} autosize label="Oddeľovač" size="sm" {...form.getInputProps('format.separator')} />
            <Textarea minRows={1} autosize label="Prefix" size="sm" {...form.getInputProps('format.prefix')} />
            <Textarea minRows={1} autosize label="Suffix" size="sm" {...form.getInputProps('format.suffix')} />
          </Fragment>
        )}
        {form.values.format.type === 'table' && (
          <Fragment>
            <Divider mt={4} color="gray.0" />
            <Stack spacing={8}>
              <P3Regular c="gray.6">Šírky stĺpcov:</P3Regular>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '88px minmax(0, 1fr) minmax(0, 1fr) 96px 116px',
                  gap: '8px 12px',
                  alignItems: 'end',
                }}
              >
                <div />
                <P4Regular c="gray.6">Stĺpec</P4Regular>
                <P4Regular c="gray.6">Nadpis stĺpca</P4Regular>
                <P4Regular c="gray.6">Hodnota</P4Regular>
                <P4Regular c="gray.6">Jednotka</P4Regular>
                {tableColumns.map((column, index) => {
                  const unit = form.values.format.widths[index]?.unit ?? 'auto';
                  const checkboxPath =
                    column.key === DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY
                      ? 'format.indexColumn.show'
                      : `format.columns.${index - 1}.show`;
                  const headingPath =
                    column.key === DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY
                      ? 'format.indexColumn.label'
                      : `format.columns.${index - 1}.label`;

                  return (
                    <Fragment key={column.key}>
                      <Checkbox
                        size="sm"
                        sx={{ alignSelf: 'center' }}
                        {...form.getInputProps(checkboxPath, { type: 'checkbox' })}
                      />
                      <P4Regular c="gray.8">{column.label}</P4Regular>
                      <TextInput size="sm" {...form.getInputProps(headingPath)} />
                      <NumberInput
                        size="sm"
                        min={1}
                        precision={0}
                        hideControls
                        disabled={unit === 'auto'}
                        placeholder={unit === 'auto' ? '-' : '0'}
                        {...form.getInputProps(`format.widths.${index}.value`)}
                      />
                      <Select
                        size="sm"
                        data={widthUnitOptions}
                        {...form.getInputProps(`format.widths.${index}.unit`)}
                      />
                    </Fragment>
                  );
                })}
              </Box>
            </Stack>
          </Fragment>
        )}
        <Group pt={8} spacing={16} position="right">
          <Button type="button" onClick={close} variant="subtle-gray">
            Zrušiť
          </Button>
          <Button type="submit" variant="primary">
            Nastaviť
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/**
 * Hook used to register the configurator for the device measurements placeholder.
 */
export default function useDeviceMeasurementsPlaceholderConfigurator(editor: Editor | null) {
  const { sections } = useFormSections();

  const openConfigurator = useCallback(() => {
    if (editor) {
      openModal({
        modalId: MODAL_ID,
        title: 'Konfigurátor tabuľky meraní',
        size: 720,
        centered: true,
        returnFocus: false,
        children: <DeviceMeasurementsConfigurator editor={editor} sections={sections} />,
      });
    }
  }, [editor, sections]);

  useEffect(() => {
    window.addEventListener(EVENT_NAME, openConfigurator);
    return () => window.removeEventListener(EVENT_NAME, openConfigurator);
  }, [openConfigurator]);
}
